import { useMemo } from 'react';

interface AuditEntry {
  id: string;
  previousStatus: string;
  nextStatus: string;
  changedBy: string;
  changedByRole: string;
  overrideReason?: string;
  source: string;
  timestamp: string;
  isMock?: boolean;
}

interface Props {
  orderId: string;
  currentStatus: string;
  createdAt: string;
}

const STATUS_TEXT: Record<string, string> = {
  draft: '草稿',
  pending: '待確認',
  need_info: '需補充資訊',
  approved: '已確認',
  renting: '出租中',
  rejected: '已拒絕',
  completed: '已完成',
  cancelled: '已取消',
};

function getStatusBadgeClass(status: string): string {
  const map: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    pending: 'bg-yellow-100 text-yellow-700',
    need_info: 'bg-orange-100 text-orange-700',
    approved: 'bg-blue-100 text-blue-700',
    renting: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-red-100 text-red-700',
    completed: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-gray-100 text-gray-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
}

function generateMockHistory(orderId: string, currentStatus: string, createdAt: string): AuditEntry[] {
  // 根據當前狀態產生合理的 mock 過渡歷程
  const baseTime = new Date(createdAt).getTime();
  const entries: AuditEntry[] = [];

  // 初始：draft / pending
  entries.push({
    id: `${orderId}-1`,
    previousStatus: '-',
    nextStatus: 'pending',
    changedBy: '系統',
    changedByRole: 'system',
    source: 'customer_submit',
    timestamp: new Date(baseTime).toISOString(),
    isMock: true,
  });

  if (['approved', 'renting', 'completed', 'cancelled', 'rejected', 'need_info'].includes(currentStatus)) {
    entries.push({
      id: `${orderId}-2`,
      previousStatus: 'pending',
      nextStatus: 'approved',
      changedBy: '王店長',
      changedByRole: 'store_manager',
      source: 'admin_console',
      timestamp: new Date(baseTime + 3600000).toISOString(),
      isMock: true,
    });
  }

  if (currentStatus === 'need_info') {
    entries.push({
      id: `${orderId}-3`,
      previousStatus: 'approved',
      nextStatus: 'need_info',
      changedBy: '李小明',
      changedByRole: 'staff',
      source: 'admin_console',
      timestamp: new Date(baseTime + 7200000).toISOString(),
      isMock: true,
    });
  }

  if (['renting', 'completed'].includes(currentStatus)) {
    entries.push({
      id: `${orderId}-3`,
      previousStatus: 'approved',
      nextStatus: 'renting',
      changedBy: '李小明',
      changedByRole: 'staff',
      source: 'pickup_verification',
      timestamp: new Date(baseTime + 86400000).toISOString(),
      isMock: true,
    });
  }

  if (currentStatus === 'completed') {
    entries.push({
      id: `${orderId}-4`,
      previousStatus: 'renting',
      nextStatus: 'completed',
      changedBy: '王店長',
      changedByRole: 'store_manager',
      source: 'return_verification',
      timestamp: new Date(baseTime + 172800000).toISOString(),
      isMock: true,
    });
  }

  if (currentStatus === 'cancelled') {
    entries.push({
      id: `${orderId}-3`,
      previousStatus: 'approved',
      nextStatus: 'cancelled',
      changedBy: '王店長',
      changedByRole: 'store_manager',
      overrideReason: '客戶拍攝計畫取消，電話確認後執行',
      source: 'manager_override',
      timestamp: new Date(baseTime + 7200000).toISOString(),
      isMock: true,
    });
  }

  if (currentStatus === 'rejected') {
    entries.push({
      id: `${orderId}-2`,
      previousStatus: 'pending',
      nextStatus: 'rejected',
      changedBy: '王店長',
      changedByRole: 'store_manager',
      overrideReason: '庫存不足且無法外調',
      source: 'admin_console',
      timestamp: new Date(baseTime + 1800000).toISOString(),
      isMock: true,
    });
  }

  return entries;
}

export default function AuditTimeline({ orderId, currentStatus, createdAt }: Props) {
  const entries = useMemo(() => generateMockHistory(orderId, currentStatus, createdAt), [orderId, currentStatus, createdAt]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <i className="ri-time-line text-teal-600"></i>
          狀態移轉稽核紀錄
        </h2>
        <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          Mock / Pending backend
        </span>
      </div>

      <div className="text-xs text-gray-500 mb-4">
        <i className="ri-information-line mr-1"></i>
        本紀錄為 append-only，任何覆寫均保留原始誤觸紀錄與覆寫原因。
      </div>

      <div className="relative pl-4">
        {/* 時間線軸 */}
        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <div className="space-y-4">
          {entries.map((entry, idx) => (
            <div key={entry.id} className="relative flex gap-4">
              {/* 時間線節點 */}
              <div
                className={`absolute left-0 w-3 h-3 rounded-full border-2 border-white -translate-x-[5px] mt-1.5 ${
                  idx === entries.length - 1 ? 'bg-teal-500' : 'bg-gray-400'
                }`}
              ></div>

              <div className="flex-1 ml-6">
                <div className="flex items-center gap-2 flex-wrap">
                  {entry.previousStatus !== '-' && (
                    <>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(entry.previousStatus)}`}>
                        {STATUS_TEXT[entry.previousStatus] || entry.previousStatus}
                      </span>
                      <i className="ri-arrow-right-line text-gray-400 text-xs"></i>
                    </>
                  )}
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(entry.nextStatus)}`}>
                    {STATUS_TEXT[entry.nextStatus] || entry.nextStatus}
                  </span>
                  {entry.overrideReason && (
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                      覆寫
                    </span>
                  )}
                </div>

                <div className="text-sm text-gray-600 mt-1">
                  {entry.overrideReason ? (
                    <span className="text-amber-700">覆寫原因：{entry.overrideReason}</span>
                  ) : (
                    <span>來源：{entry.source}</span>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                  <span>
                    <i className="ri-user-line mr-1"></i>
                    {entry.changedBy} ({entry.changedByRole})
                  </span>
                  <span>·</span>
                  <span>
                    {new Date(entry.timestamp).toLocaleString('zh-TW', {
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {entries.length === 0 && (
        <div className="text-center py-8 text-gray-400">
          <p className="text-sm">尚無移轉紀錄</p>
        </div>
      )}
    </div>
  );
}